import { userLawyerService } from '@/api';
import { Button, Icon, Typography } from '@/components/common';
import { Allowed } from '@/components/common/allowed/Allowed';
import { Input, InputEmail, InputList } from '@/components/form';
import { Options } from '@/components/ui/combobox';
import { errorFieldMessage } from '@/const';
import Icons from '@/const/icons';
import { useFilters, useNotification } from '@/hooks';
import useModal from '@/hooks/useModal';
import { Option, RegisterLawyerPaylod } from '@/interfaces';
import clsx from 'clsx';
import * as React from 'react';
import {
	FieldErrors,
	FieldValues,
	useFieldArray,
	useForm,
	UseFormClearErrors,
	UseFormRegister,
	UseFormSetError,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';

interface AdminPanelHeaderProps {
	roleOptions: Options[];
	refetch: () => void;
}

export const AdminPanelHeader: React.FC<AdminPanelHeaderProps> = ({
	roleOptions,
	refetch,
}): React.JSX.Element => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const { searchFilter, query } = useFilters();
	const { toastSuccess, toastError } = useNotification();

	// form
	const {
		register,
		control,
		handleSubmit,
		formState: { errors },
		clearErrors,
		reset,
		watch,
		setError,
		setValue,
		trigger,
	} = useForm({
		mode: 'onBlur',
	});

	const {
		fields: fieldsInvitation,
		remove: removeInvitation,
		append: appendInvitatation,
	} = useFieldArray({
		name: 'invitations',
		control,
	});

	const rules = {
		email: {
			required: { value: true, message: errorFieldMessage },
			validate: async (value: string) => {
				if (!value) return true;
				const res = await userLawyerService.getValidateLawyerEmail(value);
				if (
					res.data.value === false &&
					res.data.messages === 'Email already exists'
				) {
					return 'Email already exists';
				}
				return true;
			},
		},
		role: {
			required: { value: true, message: ' ' },
		},
	};

	const {
		Modal: ModalInvitateLawyer,
		hide: hideInvitateLawyer,
		show: showInvitateLawyer,
	} = useModal();

	const optionsFormatted = React.useMemo(() => {
		const optionsAux: Option[] = [
			{
				label: 'Select',
				name: '',
				placeholder: true,
			},
		];

		roleOptions.map((item) => {
			optionsAux.push({
				label: (item.label as string) || '',
				name: item.value || '',
			});
		});

		return optionsAux;
	}, [roleOptions]);

	React.useEffect(() => {
		if (query['search'] && watch('search') === '') {
			setValue('search', String(query['search']));
		}
	}, [query['search'], watch('search')]);

	const resetModalValues = (): void => {
		clearErrors();
		reset();
		removeInvitation();
		appendInvitatation({
			key: '',
			email: '',
			role: '',
		});
	};

	const handleInvitateLawyer = async (data: FieldValues): Promise<void> => {
		const isFormValid = await trigger(); // Espera validación
		if (!isFormValid) return;
		try {
			const newData: RegisterLawyerPaylod[] = [];
			data.invitations.map((item: { email: string; role: string }) => {
				newData.push({
					email: item.email,
					role_id: Number(item.role),
				});
			});
			await userLawyerService.postRegisterLawyer(newData);
			refetch();
			toastSuccess('Invitation sent successfully');
			hideInvitateLawyer();
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		}
	};

	const handleDropdownOpen = (index: number): void => {
		const container = containerRef.current;
		if (!container) return;
		const item = container.querySelectorAll('li')[index];
		item?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	return (
		<>
			<div className="flex flex-col lg:flex-row lg:items-center gap-2">
				<Input
					name="search"
					register={register}
					placeholder="Search member"
					iconLeft={Icons.search}
					classNameIcon={'!top-[40%] !w-4 !h-4 text-opacity-black'}
					classNameInput="pl-9 lg:!w-64 !bg-bg-white !h-8 border-none !text-sm placeholder:!text-sm"
					onChange={({ target }) => searchFilter(target.value)}
				/>
				<Allowed need={'invite_users'}>
					<Button
						label="Invite"
						iconLeft
						icon={Icons.addCircle}
						decoration="secondary"
						className="!h-12"
						onClick={() => {
							showInvitateLawyer();
							resetModalValues();
						}}
					/>
				</Allowed>
				<ModalInvitateLawyer classNameCardContainer="!px-16 !py-12">
					<form
						className="h-full flex flex-col min-h-[410px]"
						onSubmit={handleSubmit(handleInvitateLawyer)}
					>
						<Typography type="headline-3" className="text-black">
							Invite your team members
						</Typography>
						<Typography type="regular-18" className="text-black mt-6">
							Add members and assign them a role
						</Typography>
						<div
							ref={containerRef}
							className={clsx('mt-12', {
								'overflow-y-auto scroll-custom max-h-72':
									fieldsInvitation.length > 3,
							})}
						>
							<ul className="flex flex-col gap-y-4 overflow-visible">
								{fieldsInvitation.map((item, index) => (
									<li
										key={item.id}
										className="grid grid-cols-2 gap-4 items-start"
									>
										<EmailAddressInput
											errors={errors}
											index={index}
											register={register}
											rules={rules}
											watch={watch}
											setError={setError}
											setValue={setValue}
											clearErrors={clearErrors}
										/>
										<div className="flex items-center gap-4">
											<InputList
												title="Role"
												placeholder="Select"
												name={`invitations[${index}].role`}
												id={`invitations[${index}].role`}
												register={register}
												setValueInput={setValue}
												className="w-[193px]"
												options={optionsFormatted}
												rules={rules.role}
												error={(errors?.invitations as any)?.[index]?.role}
												onToggleOpen={() => handleDropdownOpen(index)}
												withOutErrorIcon={true}
												handleChange={() => {
													clearErrors(`invitations[${index}].role`);
												}}
											/>
											<Icon
												src={Icons.trash}
												className="cursor-pointer !min-w-5 !size-5 !text-main-red mt-8"
												onClick={() => removeInvitation(index)}
											/>
										</div>
									</li>
								))}
							</ul>
						</div>
						<Button
							label="Add Another Invitation"
							iconLeft
							icon={Icons.plus2}
							className="!h-12 !px-4 mt-4 mb-12 !bg-white"
							decoration="secondary"
							size="fit"
							onClick={() =>
								appendInvitatation({ key: '', email: '', role: '' })
							}
						/>
						<div className="flex items-center gap-x-8">
							<Button
								label="Cancel"
								decoration="line-primary"
								size="full"
								className="!h-16"
								onClick={hideInvitateLawyer}
							/>
							<Button
								label="Send Invitations"
								decoration="primary"
								size="full"
								className="!h-16"
								type="submit"
								disabled={fieldsInvitation.length === 0}
							/>
						</div>
					</form>
				</ModalInvitateLawyer>
			</div>
		</>
	);
};

interface EmailAddressInputProps {
	index: number;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors<FieldValues>;
	rules: any;
	watch: UseFormWatch<FieldValues>;
	setError: UseFormSetError<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	clearErrors: UseFormClearErrors<FieldValues>;
}
const EmailAddressInput: React.FC<EmailAddressInputProps> = ({
	index,
	errors,
	rules,
	register,
	watch,
	setError,
	setValue,
	clearErrors,
}) => {
	const name = `invitations[${index}].email`;
	const role = `invitations[${index}].role`;
	const email = watch(name);

	const handleValidateIfEmailExist = async (): Promise<void> => {
		if (!email) return;
		try {
			const res = await userLawyerService.getValidateLawyerEmail(email);
			if (
				res.data.value === false &&
				res.data.messages === 'Email already exists'
			) {
				setError(name, {
					message: res.data.messages,
					type: 'custom',
				});
				setError(role, {
					message: ' ',
					type: 'custom',
				});
			} else {
				clearErrors(name);
				clearErrors(role);
			}
		} catch (error) {
			console.log('Error validating email');
		}
	};

	return (
		<InputEmail
			name={name}
			id={name}
			placeholder="name@example.com"
			title="Email Address"
			register={register}
			className="w-full"
			rules={rules.email}
			error={(errors?.invitations as any)?.[index]?.email}
			onBlur={handleValidateIfEmailExist}
			setValueCustom={setValue}
			formNoValidate
		/>
	);
};
